import { Box, Stack, Typography } from '@mui/material'
import { EMPTY_VALUE } from '@repo/ui'
import type { Accuracy, AccuracyAggregate, DealForecast, ForecastFigure, RevenueFigure } from '@repo/types'

import { headerWithTerm } from 'components/display/Table'
import { InfoTooltip } from 'components/feedback/InfoTooltip'

import { shortDate } from '../InternalAnalyticsPage/components/shared'
import { moneyShort } from './dealsModel'

/**
 * VRG-692 — how a predicted / actual / accuracy figure reads, everywhere it appears (board card,
 * drawer, Leads and Companies tables, tiles). One component, so a "—" always carries its reason
 * and every number its basis and "as of" (§36: the second hand-roll is a shared component).
 */

/** `0.92×`. A ratio, not a percent: "130%" reads as "130% accurate", which is not what it says. */
export const ratioLabel = (r: number | null | undefined): string =>
  r === null || r === undefined || !Number.isFinite(r) ? EMPTY_VALUE : `${r.toFixed(2)}×`

const BASIS_LABEL: Record<string, string> = {
  manual: 'Manual',
  // VRG-701 — the deal's own (edited) calculator inputs.
  deal: 'Deal inputs',
  roi: 'Calculator',
  form: 'Form',
  subscription: 'Subscription',
  calculator: 'Calculator',
  analytics: 'Analytics',
}
export const figureBasisLabel = (basis: string | null): string | null => (basis ? (BASIS_LABEL[basis] ?? basis) : null)

const asOfLine = (iso: string | null, snapshot?: boolean): string | null =>
  iso ? `${snapshot ? 'Snapshot taken' : 'As of'} ${shortDate(iso)}` : null

/** The tooltip body for a figure: provenance, or the reason it is unknown, then as-of. */
const FigureTip = ({ figure, snapshot }: { figure: ForecastFigure; snapshot?: boolean }) => (
  <Stack spacing={1}>
    <span>{figure.amount === null ? figure.reason ?? 'Not known yet' : figure.detail ?? figureBasisLabel(figure.basis) ?? ''}</span>
    {asOfLine(figure.as_of, snapshot) && <span style={{ opacity: 0.75 }}>{asOfLine(figure.as_of, snapshot)}</span>}
  </Stack>
)

/**
 * A money figure: the annual amount (and MRR under it when `mrr` is set), or "—" with the
 * reason on hover. `snapshot` marks a frozen prediction.
 */
export const FigureValue = ({
  figure,
  term,
  showMrr,
  snapshot,
  align = 'left',
}: {
  figure: ForecastFigure | RevenueFigure
  term?: string
  showMrr?: boolean
  snapshot?: boolean
  align?: 'left' | 'right'
}) => {
  const known = figure.amount !== null
  const mrr = 'mrr' in figure ? figure.mrr : null
  return (
    <InfoTooltip term={term} text={<FigureTip figure={figure} snapshot={snapshot} />}>
      <Stack justifyContent="center" alignItems={align === 'right' ? 'flex-end' : 'flex-start'} height="100%" sx={{ minWidth: 0, cursor: 'help' }}>
        <Typography variant="body3" noWrap color={known ? 'text.primary' : 'text.secondary'}>
          {known ? moneyShort(figure.amount, figure.currency) : EMPTY_VALUE}
        </Typography>
        {showMrr && known && mrr !== null && (
          <Typography variant="body3" noWrap color="text.secondary">
            {moneyShort(mrr, figure.currency)}/mo
          </Typography>
        )}
      </Stack>
    </InfoTooltip>
  )
}

/** actual ÷ predicted, or "—" with the reason. */
export const AccuracyValue = ({ accuracy, term, align = 'left' }: { accuracy: Accuracy; term?: string; align?: 'left' | 'right' }) => (
  <InfoTooltip
    term={term}
    text={
      <Stack spacing={1}>
        <span>
          {accuracy.ratio === null
            ? accuracy.reason ?? 'Not known yet'
            : Math.abs(accuracy.ratio - 1) < 0.005
              ? 'Actual matches the prediction: a 1:1 forecast.'
              : `Actual is ${accuracy.ratio.toFixed(2)}× the prediction: the inputs ${accuracy.ratio > 1 ? 'under-promised' : 'over-promised'}.`}
        </span>
        {accuracy.as_of && <span style={{ opacity: 0.75 }}>As of {shortDate(accuracy.as_of)}</span>}
      </Stack>
    }
  >
    <Stack justifyContent="center" alignItems={align === 'right' ? 'flex-end' : 'flex-start'} height="100%" sx={{ cursor: 'help' }}>
      <Typography variant="body3" noWrap color={accuracy.ratio === null ? 'text.secondary' : 'text.primary'}>
        {ratioLabel(accuracy.ratio)}
      </Typography>
    </Stack>
  </InfoTooltip>
)

/** A tile's note for an aggregate accuracy: n, the sums, and the median. */
export const aggregateNote = (a: AccuracyAggregate, what: string): string =>
  a.n === 0
    ? `No company has both a predicted and an actual ${what} yet.`
    : `Over ${a.n} ${a.n === 1 ? 'company' : 'companies'} with both: ${moneyShort(a.actual_sum)} actual ÷ ${moneyShort(a.predicted_sum)} predicted. Median company ${ratioLabel(a.median)}.`

/** The six forecast columns' cells, keyed so the Leads and Companies tables share one definition. */
export const forecastCell = (f: DealForecast | null | undefined, key: ForecastColumnKey) => {
  if (!f) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography variant="body3" color="text.secondary">
          {EMPTY_VALUE}
        </Typography>
      </Box>
    )
  }
  switch (key) {
    case 'predicted_arr':
      return <FigureValue figure={f.predicted_arr} showMrr snapshot={f.predicted_arr.source === 'snapshot'} />
    case 'actual_arr':
      return <FigureValue figure={f.actual_arr} showMrr />
    case 'revenue_accuracy':
      return <AccuracyValue accuracy={f.revenue_accuracy} />
    case 'predicted_roi':
      return <FigureValue figure={f.predicted_roi} snapshot={f.predicted_roi.source === 'snapshot'} />
    case 'actual_roi':
      return <FigureValue figure={f.actual_roi} />
    case 'roi_accuracy':
      return <AccuracyValue accuracy={f.roi_accuracy} />
  }
}

export const FORECAST_COLUMNS = [
  // Widths are for the HEADER (§49): label + sort icon/menu (~63px) + the info glyph (18px).
  { key: 'predicted_arr', headerName: 'Predicted ARR', term: 'deal-anticipated-value', width: 185 },
  { key: 'actual_arr', headerName: 'Committed ARR', term: 'sales-committed-arr', width: 190 },
  { key: 'revenue_accuracy', headerName: 'Revenue accuracy', term: 'sales-forecast-accuracy', width: 205 },
  { key: 'predicted_roi', headerName: 'Predicted ROI', term: 'brand-predicted-roi', width: 185 },
  { key: 'actual_roi', headerName: 'Actual ROI', term: 'brand-actual-roi', width: 165 },
  { key: 'roi_accuracy', headerName: 'ROI accuracy', term: 'brand-roi-accuracy', width: 180 },
] as const
export type ForecastColumnKey = (typeof FORECAST_COLUMNS)[number]['key']

/** The numeric value a column sorts by: the amount / ratio, unknowns last. */
export const forecastSortValue = (f: DealForecast | null | undefined, key: ForecastColumnKey): number | null => {
  if (!f) return null
  switch (key) {
    case 'predicted_arr':
      return f.predicted_arr.amount
    case 'actual_arr':
      return f.actual_arr.amount
    case 'revenue_accuracy':
      return f.revenue_accuracy.ratio
    case 'predicted_roi':
      return f.predicted_roi.amount
    case 'actual_roi':
      return f.actual_roi.amount
    case 'roi_accuracy':
      return f.roi_accuracy.ratio
  }
}

/**
 * The six forecast columns as DataGrid column definitions, for any row that can reach a deal
 * forecast. The Leads and the Companies tables both spread this, so they cannot disagree (§71).
 */
export const forecastColumns = <R,>(get: (row: R) => DealForecast | null | undefined) =>
  FORECAST_COLUMNS.map((c) => ({
    field: `forecast_${c.key}`,
    headerName: c.headerName,
    type: 'number' as const,
    headerAlign: 'left' as const,
    align: 'left' as const,
    width: c.width,
    renderHeader: headerWithTerm(c.headerName, c.term),
    valueGetter: (_v: unknown, row: R) => forecastSortValue(get(row), c.key),
    renderCell: ({ row }: { row: R }) => forecastCell(get(row), c.key),
  }))
